<script setup lang="ts">
import { ref } from "vue";
import { format } from "date-fns";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VLabel from "@magnit/core/src/components/VLabel/VLabel.vue";
import VModal from "@magnit/core/src/components/VModal/VModal.vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VEmpty from "@magnit/core/src/containers/VEmpty/VEmpty.vue";
import VCatalogBadge from "@magnit/unit-catalog/src/components/VCatalogBadge.vue";
import VIllustrationLoyaltyV1 from "@magnit/illustrations/src/assets/svg/illustrations/v1/112-loyalty.svg";
import VIllustrationSaleBase from "@magnit/illustrations/src/assets/svg/illustrations/base/112-sale-magnit.svg";
import type {
  IProductDetailsBase,
  IProductDetailsProps,
} from "../types/product-details";
import type { ICatalogBFFDetailedBadge } from "../types/goods";
import ActionDate from "./ActionDate.vue";
import useProduct from "../composables/useProduct";

interface IProductDetailsBadgesProps extends IProductDetailsBase {
  theme?: IProductDetailsProps["theme"];
}

const props = defineProps<IProductDetailsBadgesProps>();
const { productBadges, productDiscountBadge, productProfitBadge } = useProduct(
  props.product,
);
const selectedBadge = ref<ICatalogBFFDetailedBadge | null>(null);
const onBadgeClick = (badge: ICatalogBFFDetailedBadge) =>
  (selectedBadge.value = badge.description ? badge : null);
const onCloseModal = () => (selectedBadge.value = null);

const openModalProfit = ref(false);
const onCloseModalProfit = () => (openModalProfit.value = false);
</script>

<template>
  <div
    v-if="productBadges.length || productProfitBadge"
    class="product-details-badges"
  >
    <VLabel
      v-if="productProfitBadge"
      class="product-details-badges__item"
      theme="discount"
      arrow
      @click="openModalProfit = true"
    >
      <span v-html="productProfitBadge.text"></span>
    </VLabel>
    <VCatalogBadge
      v-if="productDiscountBadge"
      class="product-details-badges__item"
      :badge="productDiscountBadge"
    />
    <VCatalogBadge
      v-for="badge in productBadges"
      :key="badge.text"
      class="product-details-badges__item"
      :badge="badge"
      @click:badge="onBadgeClick"
    />
    <VModal
      id="badge"
      :active="!!selectedBadge"
      :open="!!selectedBadge"
      :type="{ xs: 'bottom-sheet', m: 'modal', l: 'modal' }"
      :closable="false"
      wrapper-class="badge-modal"
      @close="onCloseModal"
    >
      <div v-if="selectedBadge">
        <div class="badge-modal__info">
          <ActionDate
            v-if="selectedBadge.endDate"
            :end="selectedBadge.endDate"
          />
          <VCatalogBadge
            class="badge-modal__badge"
            :badge="selectedBadge"
            hide-arrow
          />
        </div>

        <VText
          v-if="selectedBadge.descriptionHeader"
          tag="div"
          font="title-large"
          class="badge-modal__header"
        >
          {{ selectedBadge.descriptionHeader }}
        </VText>
        <VText
          v-if="selectedBadge.description"
          tag="div"
          font="body-small-regular"
        >
          <span v-html="selectedBadge.description" />
        </VText>
      </div>
      <template #footer>
        <VButton theme="secondary" full-width @click="onCloseModal">
          Закрыть
        </VButton>
      </template>
    </VModal>
    <VModal
      v-if="productProfitBadge"
      id="profit-modal"
      wrapper-class="profit-modal"
      :active="openModalProfit"
      :open="openModalProfit"
      :type="{ xs: 'bottom-sheet', m: 'modal' }"
      @close="onCloseModalProfit"
    >
      <div>
        <VEmpty
          title="У нас дешевле"
          description="Посчитали разницу в сравнении с другими магазинами"
          :icon="
            theme === 'V1' ? VIllustrationLoyaltyV1 : VIllustrationSaleBase
          "
        />

        <VText tag="div" font="caption-large-regular-low" color="secondary">
          <span class="profit-modal__subdesc">
            Цены актуальны на {{ format(new Date(), "dd.MM.yyyy") }} и указаны
            после применения всех скидок.
            <a
              v-if="theme === 'V1'"
              href="https://v1.market/mechanics-price-comparison"
              >Подробнее о механике сравнения цен</a
            >
          </span>
        </VText>
      </div>
      <template #footer>
        <VButton full-width @click="onCloseModalProfit"> Отлично </VButton>
      </template>
    </VModal>
  </div>
</template>

<style lang="postcss" scoped>
.product-details-badges {
  margin-bottom: var(--pl-unit-x3);
  display: flex;
  flex-wrap: wrap;
  gap: var(--pl-unit-x1);

  @media (--pl-viewport-from-l) {
    margin-bottom: var(--pl-unit-x4);
  }

  &__item {
    margin-bottom: var(--pl-unit-x1);
    margin-right: var(--pl-unit-x1);
  }
}

.badge-modal {
  &__info {
    display: flex;
    flex-direction: column;
    gap: var(--pl-unit-x4);
    padding: var(--pl-unit-x4) 0;
  }

  &__badge {
    width: max-content;
  }

  &__header {
    margin-bottom: var(--pl-unit-x2);
  }
}

:global(.profit-modal) {
  &__subdesc {
    text-align: center;
  }
}
</style>
